import { useAtomValue } from 'jotai';
import { useEffect, useState } from 'react';
import TagManager from 'react-gtm-module';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { dealsNavHref } from 'src/components/deals/dealFilters';
import { anyBlockingModalOpenAtom, dealsVendorAtom, vendorHasDealsAtom } from 'src/store';
import { markDealsNagDismissedForever, useShowDealsNag } from './useShowDealsNag';

const NAG_IMAGE_URL = 'https://assets.share-a-cart.com/images/next/hear-ye.webp';

type Props = {
  /** Source slug appended to /deals?from= (e.g. "nag-get"). */
  source: string;
  /**
   * Set on the CartHistory mount only: wait for the WelcomeModal control atom
   * (showOtherModalsAtom) before showing, so the welcome modal keeps precedence.
   */
  deferToWelcomeModal?: boolean;
};

export const DealsNag = ({ source, deferToWelcomeModal }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dealsVendor = useAtomValue(dealsVendorAtom);
  const vendorHasDeals = useAtomValue(vendorHasDealsAtom);
  const anyBlockingModalOpen = useAtomValue(anyBlockingModalOpenAtom);

  const [isOpen, setIsOpen] = useState(false);
  const [entered, setEntered] = useState(false);

  useShowDealsNag({ setIsOpen, deferToWelcomeModal });

  // Entrance transition once shown.
  useEffect(() => {
    if (!isOpen) {
      setEntered(false);
      return;
    }
    const id = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(id);
  }, [isOpen]);

  // Prevent wheel/touch/keyboard scrolling while the nag is open so the Deals button
  // stays in view and the caret keeps pointing at it — without hiding the scrollbar
  // (no overflow:hidden → no layout shift). Clicks are intercepted by the overlay
  // (click-off dismiss). Listeners removed on dismiss / unmount.
  useEffect(() => {
    if (!isOpen) return;
    const scrollKeys = new Set([' ', 'PageUp', 'PageDown', 'End', 'Home', 'ArrowUp', 'ArrowDown']);
    const preventScroll = (e: Event) => e.preventDefault();
    const preventScrollKeys = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      const tag = t?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || t?.isContentEditable) {
        return;
      }
      // Space activates a focused button/link — don't hijack it.
      if (e.key === ' ' && (tag === 'BUTTON' || tag === 'A')) return;
      if (scrollKeys.has(e.key)) e.preventDefault();
    };
    window.addEventListener('wheel', preventScroll, { passive: false });
    window.addEventListener('touchmove', preventScroll, { passive: false });
    window.addEventListener('keydown', preventScrollKeys);
    return () => {
      window.removeEventListener('wheel', preventScroll);
      window.removeEventListener('touchmove', preventScroll);
      window.removeEventListener('keydown', preventScrollKeys);
    };
  }, [isOpen]);

  // Belt-and-suspenders with the hook's yield effect: never paint over a modal.
  if (!isOpen || anyBlockingModalOpen) return null;

  // Soft dismiss (X / click-off): the 30-day timer was set when the nag opened,
  // so it simply won't reappear for 30 days.
  const handleClose = () => setIsOpen(false);

  const handleExploreOffers = () => {
    TagManager.dataLayer({
      dataLayer: {
        event: 'Deals',
        eventCategory: 'engagement',
        eventLabel: 'Deals Nag - Explore Offers',
        eventValue: { source },
      },
    });

    setIsOpen(false);
    navigate(dealsNavHref(dealsVendor, vendorHasDeals, source));
  };

  const handleDontShowAgain = () => {
    markDealsNagDismissedForever();
    setIsOpen(false);
  };

  return (
    <div
      className='fixed inset-0 z-50'
      role='dialog'
      aria-modal='true'
      aria-label={t('deals.nag.title')}
    >
      {/* Transparent click-catcher → soft dismiss */}
      <button
        type='button'
        aria-label={t('common.close')}
        onClick={handleClose}
        className='absolute inset-0 h-full w-full cursor-default'
      />

      {/* Speech bubble, pinned top-right just below the navbar; the tail points
          up at the always-visible Deals button. */}
      <div
        className={`absolute right-0 top-[76px] flex w-[320px] max-w-[calc(100vw-1rem)] flex-col items-end transition-all duration-300 ease-out xs:right-2 sm:right-4 md:right-6 medLg:right-2 lg:right-8 xl:right-10 ${
          entered ? 'translate-y-0 opacity-100' : '-translate-y-2 opacity-0'
        }`}
      >
        {/* Tail */}
        <div className='mr-3 h-0 w-0 border-x-[18px] border-b-[18px] border-x-transparent border-b-[#FBE9BA]' />

        {/* Card */}
        <div className='relative flex w-full flex-col items-center gap-5 rounded-xl bg-[#FBE9BA] p-5 shadow-[0px_8px_8px_rgba(0,0,0,0.3)]'>
          {/* Close (X) — added on top of the Figma design */}
          <button
            type='button'
            onClick={handleClose}
            aria-label={t('common.close')}
            className='absolute right-3 top-3 inline-flex rounded-md text-[#977109] transition-colors hover:text-primary-dark'
          >
            <svg
              className='h-5 w-5'
              xmlns='http://www.w3.org/2000/svg'
              viewBox='0 0 20 20'
              fill='currentColor'
              aria-hidden='true'
            >
              <path
                fillRule='evenodd'
                d='M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z'
                clipRule='evenodd'
              />
            </svg>
          </button>

          <img
            src={NAG_IMAGE_URL}
            alt=''
            width={160}
            height={160}
            className='h-auto w-[160px] rounded-lg object-cover'
          />

          <div className='flex w-full flex-col items-center gap-3 text-center'>
            <p className='font-primary text-[20px] font-semibold leading-[1.3] text-primary-dark'>
              <span className='text-[#977109]'>{t('deals.nag.badge_new')} </span>
              {t('deals.nag.title')}
            </p>
            <p className='font-secondary text-[14px] leading-[1.5] text-primary-dark'>
              {t('deals.nag.body')}
            </p>
          </div>

          <div className='flex w-full flex-col items-center gap-3'>
            <button
              type='button'
              onClick={handleExploreOffers}
              className='button-primary h-12 w-full rounded-full'
            >
              {t('deals.nag.explore_deals')}
            </button>
            <button
              type='button'
              onClick={handleDontShowAgain}
              className='font-secondary text-[14px] font-semibold leading-[1.5] text-primary transition-opacity hover:opacity-80'
            >
              {t('deals.nag.dont_show_again')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
